/**
 * Notification Service - Notificaciones Push para recordatorios
 *
 * Diseñado para neurodivergencia:
 * - Recordatorios gentiles, no intrusivos
 * - Respeta horarios de no molestar
 * - Mensajes claros y accionables
 * - El usuario tiene control total
 */

// Estado de permisos
type NotificationPermission = 'granted' | 'denied' | 'default';

interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
}

/**
 * Verifica si las notificaciones estan soportadas
 */
export function isNotificationSupported(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator;
}

/**
 * Obtiene el estado actual del permiso
 */
export function getNotificationPermission(): NotificationPermission {
  if (!isNotificationSupported()) return 'denied';
  return Notification.permission as NotificationPermission;
}

/**
 * Solicita permiso para mostrar notificaciones
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isNotificationSupported()) {
    console.warn('Notifications not supported');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission as NotificationPermission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
}

/**
 * Muestra una notificacion
 */
export async function showNotification(options: NotificationOptions): Promise<void> {
  const permission = getNotificationPermission();

  if (permission !== 'granted') {
    console.warn('Notification permission not granted');
    return;
  }

  try {
    // Intentar usar Service Worker para notificaciones persistentes
    const registration = await navigator.serviceWorker.ready;

    const swNotificationOptions: globalThis.NotificationOptions & { vibrate?: number[] } = {
      body: options.body,
      icon: options.icon ?? '/icon-192.png',
      badge: '/icon-72.png',
      tag: options.tag ?? 'exocortex-notification',
      requireInteraction: options.requireInteraction ?? false,
      silent: options.silent ?? false,
    };

    // Agregar vibrate solo si es soportado (algunos navegadores)
    if ('vibrate' in navigator) {
      swNotificationOptions.vibrate = [100, 50, 100];
    }

    await registration.showNotification(options.title, swNotificationOptions);
  } catch (error) {
    // Fallback a notificacion simple
    console.warn('SW notification failed, using fallback:', error);

    const fallbackOptions: globalThis.NotificationOptions = {
      body: options.body,
      icon: options.icon ?? '/icon-192.png',
    };

    if (options.tag) {
      fallbackOptions.tag = options.tag;
    }
    if (options.silent !== undefined) {
      fallbackOptions.silent = options.silent;
    }

    new Notification(options.title, fallbackOptions);
  }
}

/**
 * Tipos de recordatorios predefinidos
 */
export type ReminderType =
  | 'morning_start'      // Inicio del dia
  | 'protocol_reminder'  // Recordatorio de protocolo
  | 'break_reminder'     // Hora de descanso
  | 'evening_check'      // Check-in de la tarde
  | 'streak_risk';       // Racha en riesgo

interface ReminderConfig {
  type: ReminderType;
  title: string;
  body: string;
  tag: string;
}

const REMINDER_MESSAGES: Record<ReminderType, ReminderConfig> = {
  morning_start: {
    type: 'morning_start',
    title: 'Buenos dias',
    body: 'Es un buen momento para revisar tus protocolos del dia. Sin prisa.',
    tag: 'exo-morning',
  },
  protocol_reminder: {
    type: 'protocol_reminder',
    title: 'Recordatorio suave',
    body: 'Tienes protocolos pendientes. Cuando puedas, sin estres.',
    tag: 'exo-protocol',
  },
  break_reminder: {
    type: 'break_reminder',
    title: 'Hora de descansar',
    body: 'Has estado trabajando bien. Tu cerebro necesita una pausa.',
    tag: 'exo-break',
  },
  evening_check: {
    type: 'evening_check',
    title: 'Check-in de la tarde',
    body: 'Como va tu dia? Revisa tu progreso cuando quieras.',
    tag: 'exo-evening',
  },
  streak_risk: {
    type: 'streak_risk',
    title: 'Tu racha esta en riesgo',
    body: 'Completa al menos una tarea para mantener tu racha. Tu decides.',
    tag: 'exo-streak',
  },
};

/**
 * Envia un recordatorio predefinido
 */
export async function sendReminder(type: ReminderType): Promise<void> {
  const config = REMINDER_MESSAGES[type];
  await showNotification({
    title: config.title,
    body: config.body,
    tag: config.tag,
    requireInteraction: type === 'streak_risk',
  });
}

/**
 * Programa un recordatorio para una hora especifica
 */
export function scheduleReminder(
  type: ReminderType,
  scheduledTime: Date
): number {
  const now = Date.now();
  const delay = scheduledTime.getTime() - now;

  if (delay <= 0) {
    console.warn('Scheduled time is in the past');
    return -1;
  }

  const timeoutId = window.setTimeout(() => {
    sendReminder(type);
  }, delay);

  return timeoutId;
}

/**
 * Cancela un recordatorio programado
 */
export function cancelReminder(timeoutId: number): void {
  window.clearTimeout(timeoutId);
}

/**
 * Programar recordatorios diarios basados en preferencias del usuario
 */
export interface DailyReminderConfig {
  morningEnabled: boolean;
  morningTime: string; // HH:MM format
  eveningEnabled: boolean;
  eveningTime: string;
  breakReminders: boolean;
  breakInterval: number; // minutos
}

let activeReminders: number[] = [];

export function setupDailyReminders(config: DailyReminderConfig): void {
  // Cancelar recordatorios existentes
  activeReminders.forEach(cancelReminder);
  activeReminders = [];

  const now = new Date();
  const today = now.toISOString().split('T')[0];

  // Recordatorio matutino
  if (config.morningEnabled && config.morningTime) {
    const morningDate = new Date(`${today}T${config.morningTime}:00`);
    if (morningDate > now) {
      const id = scheduleReminder('morning_start', morningDate);
      if (id > 0) activeReminders.push(id);
    }
  }

  // Recordatorio vespertino
  if (config.eveningEnabled && config.eveningTime) {
    const eveningDate = new Date(`${today}T${config.eveningTime}:00`);
    if (eveningDate > now) {
      const id = scheduleReminder('evening_check', eveningDate);
      if (id > 0) activeReminders.push(id);
    }
  }
}

/**
 * Limpia todos los recordatorios activos
 */
export function clearAllReminders(): void {
  activeReminders.forEach(cancelReminder);
  activeReminders = [];
}
